/**
 * Persistence Guard Utility
 *
 * This utility helps maintain data persistence when making code changes.
 * It provides methods to verify that persistence-critical functionality
 * remains intact during development.
 */

import { User } from "../types/auth";

export interface PersistenceCheckResult {
  isValid: boolean;
  missingFields: string[];
  errors: string[];
}

/**
 * Verifies that all essential user data fields are properly mapped
 * between frontend state and backend API
 */
export const verifyUserDataMapping = (
  profileData: Record<string, unknown>,
  user: User | null,
): PersistenceCheckResult => {
  const result: PersistenceCheckResult = {
    isValid: true,
    missingFields: [],
    errors: [],
  };

  // Essential fields that must be persisted (optional fields excluded from strict check)
  // Note: dateOfBirth is split into birthDay, birthMonth, birthYear in profileData
  const essentialFields = [
    "firstName",
    "lastName",
    "email",
    // dateOfBirth is handled separately as it's split into 3 fields
  ];

  // Check if profileData has all essential fields
  essentialFields.forEach((field) => {
    if (!(field in profileData)) {
      result.missingFields.push(field);
      result.isValid = false;
    }
  });

  // Check if user data is properly initialized in profileData
  if (user) {
    const mappingChecks = [
      { userField: "firstName", profileField: "firstName" },
      { userField: "lastName", profileField: "lastName" },
      { userField: "email", profileField: "email" },
      { userField: "phoneNumber", profileField: "phone" },
      { userField: "nationality", profileField: "nationality" },
      { userField: "address", profileField: "street" }, // Special case: address split into street/houseNumber
      { userField: "city", profileField: "city" },
      { userField: "country", profileField: "country" },
      { userField: "postalCode", profileField: "postalCode" },
      // dateOfBirth is split into birthDay, birthMonth, birthYear - not checked here
    ];

    mappingChecks.forEach(({ userField, profileField }) => {
      if (user[userField as keyof User] && !profileData[profileField]) {
        result.errors.push(
          `User field '${userField}' is not properly mapped to profileData field '${profileField}'`,
        );
        result.isValid = false;
      }
    });
  }
  return result;
};

/**
 * Verifies that update data contains all modified fields
 */
export const verifyUpdateData = (
  updateData: Record<string, unknown>,
  _originalData: Record<string, unknown>,
  changedFields: string[],
): PersistenceCheckResult => {
  const result: PersistenceCheckResult = {
    isValid: true,
    missingFields: [],
    errors: [],
  };

  // Check if all changed fields are included in update data
  changedFields.forEach((field) => {
    if (!(field in updateData)) {
      result.missingFields.push(field);
      result.isValid = false;
    }
  });

  return result;
};

/**
 * Validates API response structure
 */
export const verifyApiResponse = (
  response: unknown,
): PersistenceCheckResult => {
  const result: PersistenceCheckResult = {
    isValid: true,
    missingFields: [],
    errors: [],
  };

  if (!response || typeof response !== "object") {
    result.errors.push("API response is null or undefined");
    result.isValid = false;
    return result;
  }

  // The backend envelope is `{ success, data, meta }` / `{ success, error }`.
  // This function's whole job is to verify that shape, so it has to read the
  // fields off an `unknown` - narrow once here rather than per access.
  const envelope = response as {
    success?: unknown;
    data?: { user?: unknown } | null;
  };

  if (typeof envelope.success !== "boolean") {
    result.errors.push("API response missing success field");
    result.isValid = false;
  }

  if (envelope.success && !envelope.data) {
    result.errors.push("Successful API response missing data field");
    result.isValid = false;
  }

  if (envelope.success && envelope.data && !envelope.data.user) {
    result.errors.push("API response data missing user field");
    result.isValid = false;
  }

  return result;
};

/**
 * Comprehensive persistence check for profile updates
 */
export const performPersistenceCheck = (
  profileData: Record<string, unknown>,
  user: User | null,
  updateData: Record<string, unknown>,
): PersistenceCheckResult => {
  const mappingCheck = verifyUserDataMapping(profileData, user);
  const updateCheck = verifyUpdateData(
    updateData,
    profileData,
    Object.keys(updateData),
  );

  return {
    isValid: mappingCheck.isValid && updateCheck.isValid,
    missingFields: [
      ...mappingCheck.missingFields,
      ...updateCheck.missingFields,
    ],
    errors: [...mappingCheck.errors, ...updateCheck.errors],
  };
};

/**
 * Development mode persistence checker
 * Logs warnings and errors to console in development
 */
export const devPersistenceGuard = {
  checkProfileData: (
    profileData: Record<string, unknown>,
    user: User | null,
  ) => {
    if (process.env.NODE_ENV === "development") {
      const result = verifyUserDataMapping(profileData, user);
      if (!result.isValid) {
        console.warn("🚨 Persistence Guard Warning:", {
          missingFields: result.missingFields,
          errors: result.errors,
        });
      }
    }
  },

  checkApiResponse: (response: unknown) => {
    if (process.env.NODE_ENV === "development") {
      const result = verifyApiResponse(response);
      if (!result.isValid) {
        console.error("🚨 API Response Persistence Error:", {
          errors: result.errors,
        });
      }
    }
  },

  checkUpdateData: (
    updateData: Record<string, unknown>,
    profileData: Record<string, unknown>,
  ) => {
    if (process.env.NODE_ENV === "development") {
      const changedFields = Object.keys(updateData);
      const result = verifyUpdateData(updateData, profileData, changedFields);
      if (!result.isValid) {
        console.error("🚨 Update Data Persistence Error:", {
          missingFields: result.missingFields,
          errors: result.errors,
        });
      }
    }
  },
};
